import { FC, useMemo, useState, useEffect } from "react";
import { IonPage, IonContent, IonSelect, IonSelectOption, IonItem, IonLabel, useIonRouter } from "@ionic/react";
import { useParams } from "react-router";
import HeaderComponent from "../../../components/HeaderComponent";
import { routes } from "../../../route/constants";
import { useMatch, useMatches } from "../../../components/hooks/match";
import { useFitnessDataContext } from "../../../components/hooks";
import { useProfile } from "../../../components/hooks";
import { GpsFilteredData, HrData, HalfEvent } from "refsix-js-models";
import { matchesSortedByDate } from "../../../services/matchService";
import moment from "moment";
import "./activityGraphComparison.css";
import {
    normalizeData,
    calculateDistancePerMinute,
    calculatePeakSpeedPerMinute,
    calculateHeartRatePerMinute,
    calculateHRZone45PercentagePerMinute,
    calculateCompositeIntensity,
} from "../../../utils/intensityCalculation";

interface ActivityGraphProps {
    data: number[];
    label: string;
    color?: string;
}

const ActivityGraph: FC<ActivityGraphProps> = ({ data, label, color = '#0eaa0b' }) => {
    // Sanitize label for use in SVG ID (remove spaces and special characters)
    const sanitizedLabel = label.replace(/[^a-zA-Z0-9]/g, '-');

    return (
        <div className="activity-graph-container">
            <h2 className="activity-graph-label">{label}</h2>
            <svg
                width="120"
                height="calc(100vh - 120px)"
                style={{ minHeight: '600px' }}
                preserveAspectRatio="none"
                viewBox="0 0 100 100"
            >
                <defs>
                    <linearGradient id={`waveGradient-${sanitizedLabel}`} x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor={color} stopOpacity="0.2" />
                        <stop offset="50%" stopColor={color} stopOpacity="0.5" />
                        <stop offset="100%" stopColor={color} stopOpacity="0.2" />
                    </linearGradient>
                </defs>

                <path
                    d={(() => {
                        const center = 50;
                        const maxAmplitude = 45;

                        let pathPoints: Array<{ y: number; leftX: number; rightX: number }> = [];

                        data.forEach((value, i) => {
                            const y = (i / data.length) * 100;
                            const amplitude = value * maxAmplitude;

                            const leftX = center - amplitude;
                            const rightX = center + amplitude;

                            pathPoints.push({ y, leftX, rightX });
                        });

                        let path = `M ${pathPoints[0].leftX} ${pathPoints[0].y}`;

                        for (let i = 1; i < pathPoints.length; i++) {
                            if (i < pathPoints.length - 1) {
                                const currPoint = pathPoints[i];
                                const nextPoint = pathPoints[i + 1];

                                const cpX = currPoint.leftX;
                                const cpY = currPoint.y;

                                path += ` Q ${cpX} ${cpY}, ${(currPoint.leftX + nextPoint.leftX) / 2} ${(currPoint.y + nextPoint.y) / 2}`;
                            } else {
                                path += ` L ${pathPoints[i].leftX} ${pathPoints[i].y}`;
                            }
                        }

                        for (let i = pathPoints.length - 1; i >= 0; i--) {
                            if (i > 0) {
                                const currPoint = pathPoints[i];
                                const prevPoint = pathPoints[i - 1];

                                const cpX = currPoint.rightX;
                                const cpY = currPoint.y;

                                path += ` Q ${cpX} ${cpY}, ${(currPoint.rightX + prevPoint.rightX) / 2} ${(currPoint.y + prevPoint.y) / 2}`;
                            } else {
                                path += ` L ${pathPoints[i].rightX} ${pathPoints[i].y}`;
                            }
                        }

                        path += ' Z';
                        return path;
                    })()}
                    fill={`url(#waveGradient-${sanitizedLabel})`}
                    opacity="0.7"
                />
                <line x1="50" y1="0" x2="50" y2="100" stroke={color} strokeWidth="1" opacity="0.2" />
            </svg>
        </div>
    );
};

const ActivityGraphComparison: FC = () => {
    const { id } = useParams<{ id: string }>();
    const match = useMatch(id);
    const profile = useProfile();
    const { gpsData, hrData, loading } = useFitnessDataContext();
    const router = useIonRouter();

    // Match selector state
    const allMatches = useMatches();
    const [selectedMatchId, setSelectedMatchId] = useState<string>(id);

    // Filter matches with fitness data (GPS or HR)
    const eligibleMatches = useMemo(() => {
        if (!allMatches) return [];

        return matchesSortedByDate(allMatches)
            .reverse() // Newest first
            .filter((match) => match.hasTracking || match.hasHeartRate);
    }, [allMatches]);

    // Calculate max heart rate (default to 190 if no profile DOB available)
    const maxHR = useMemo(() => {
        if (profile?.dob && match?.date) {
            const birthDate = new Date(profile.dob);
            const matchDate = new Date(match.date);
            const age = matchDate.getFullYear() - birthDate.getFullYear();
            return 220 - age;
        }
        return 190; // Default max HR if age cannot be calculated
    }, [profile?.dob, match?.date]);

    // Generate activity data for Graph 1: Distance per minute
    const activityData1 = useMemo(() => {
        if (!gpsData?.geoPoints || !gpsData.matchTimings) {
            return [];
        }

        const distancePerMinute = calculateDistancePerMinute(gpsData, gpsData.matchTimings);
        return normalizeData(distancePerMinute);
    }, [gpsData]);

    // Generate activity data for Graph 2: Heart Rate per minute
    const activityData2 = useMemo(() => {
        if (!hrData?.heartRateValues || !gpsData?.matchTimings) {
            return [];
        }

        const heartRatePerMinute = calculateHeartRatePerMinute(hrData, gpsData.matchTimings);
        return normalizeData(heartRatePerMinute);
    }, [hrData, gpsData?.matchTimings]);

    // Generate activity data for Graph 3: Peak Speed per minute
    const activityData3 = useMemo(() => {
        if (!gpsData?.geoPoints || !gpsData.matchTimings) {
            return [];
        }

        const peakSpeedPerMinute = calculatePeakSpeedPerMinute(gpsData, gpsData.matchTimings);
        return normalizeData(peakSpeedPerMinute);
    }, [gpsData]);

    // Generate activity data for Graph 4: HR Zone 4+5 percentage per minute
    const activityData4 = useMemo(() => {
        if (!hrData?.heartRateValues || !gpsData?.matchTimings) {
            return [];
        }

        const zone45Percentage = calculateHRZone45PercentagePerMinute(hrData, gpsData.matchTimings, maxHR);
        return normalizeData(zone45Percentage);
    }, [hrData, gpsData?.matchTimings, maxHR]);

    // Generate activity data for Graph 5: Composite Intensity
    const activityData5 = useMemo(() => {
        if (!gpsData?.geoPoints || !gpsData.matchTimings) {
            return [];
        }

        const compositeIntensity = calculateCompositeIntensity(
            gpsData,
            hrData,
            gpsData.matchTimings,
            maxHR
        );
        // Note: Don't normalize again - already in 0-1 range after sharpening
        return compositeIntensity;
    }, [gpsData, hrData, maxHR]);

    // Check if we have any fitness data
    const hasFitnessData = activityData1.length > 0 || activityData2.length > 0 || activityData3.length > 0 || activityData4.length > 0 || activityData5.length > 0;

    // Handle match selection change
    const handleMatchChange = (newMatchId: string) => {
        if (!newMatchId) return;

        setSelectedMatchId(newMatchId);

        // Update URL to reflect selected match
        router.push(
            routes.activityGraphComparison + `/${newMatchId}`,
            'none',
            'replace'
        );
    };

    // Sync selected match with URL parameter
    useEffect(() => {
        if (id && id !== selectedMatchId) {
            setSelectedMatchId(id);
        }
    }, [id, selectedMatchId]);

    // Validate selected match exists in eligible matches
    useEffect(() => {
        if (eligibleMatches.length > 0) {
            const matchExists = eligibleMatches.some(m => m._id === selectedMatchId);

            // If selected match doesn't have fitness data, select first eligible match
            if (!matchExists && selectedMatchId) {
                const firstMatch = eligibleMatches[0];
                if (firstMatch?._id) {
                    setSelectedMatchId(firstMatch._id);
                    router.push(
                        routes.activityGraphComparison + `/${firstMatch._id}`,
                        'none',
                        'replace'
                    );
                }
            }
        }
    }, [eligibleMatches, selectedMatchId, router]);

    // If no ID in URL but eligible matches exist, redirect to first match
    useEffect(() => {
        if (!id && eligibleMatches.length > 0) {
            const firstMatch = eligibleMatches[0];
            if (firstMatch?._id) {
                router.push(
                    routes.activityGraphComparison + `/${firstMatch._id}`,
                    'none',
                    'replace'
                );
            }
        }
    }, [id, eligibleMatches, router]);

    // Loading state
    if (!match || loading) {
        return (
            <IonPage>
                <HeaderComponent
                    title="Activity Graphs"
                    showBackButton
                    defaultHref={routes.tabResults}
                />
                <IonContent>
                    <div style={{ padding: '20px', textAlign: 'center', color: 'white' }}>
                        {loading ? 'Loading fitness data...' : 'Loading match data...'}
                    </div>
                </IonContent>
            </IonPage>
        );
    }

    return (
        <IonPage>
            <HeaderComponent
                title="Activity Graphs"
                showBackButton
                defaultHref={routes.tabFixtures}
            />
            <IonContent className="activity-graph-comparison-content">
                {/* Match Selector Dropdown */}
                {eligibleMatches.length > 0 && (
                    <div className="match-selector-container" style={{ padding: '1rem', backgroundColor: '#1f2937', borderBottom: '1px solid #374151' }}>
                        <IonItem lines="none" style={{ '--background': '#1f2937' } as React.CSSProperties}>
                            <IonLabel position="stacked" style={{ color: 'white', marginBottom: '0.5rem', fontWeight: '500' }}>
                                Select Match
                            </IonLabel>
                            <IonSelect
                                value={selectedMatchId}
                                interface="action-sheet"
                                placeholder="Select a match"
                                onIonChange={(e) => handleMatchChange(e.detail.value)}
                                style={{ width: '100%', color: 'white' }}
                                data-testid="activity-graph-match-selector"
                            >
                                {eligibleMatches.map((match) => (
                                    <IonSelectOption key={match._id} value={match._id}>
                                        {`${match.homeTeam} vs ${match.awayTeam} ${moment(match.date).format('DD/MM/YYYY HH:mm')}`}
                                    </IonSelectOption>
                                ))}
                            </IonSelect>
                        </IonItem>
                    </div>
                )}

                {eligibleMatches.length === 0 ? (
                    <div style={{
                        padding: '1rem',
                        textAlign: 'center',
                        color: 'white',
                        fontSize: '1.2rem',
                        marginTop: '50%'
                    }}>
                        No matches with fitness data available
                    </div>
                ) : !hasFitnessData ? (
                    <div style={{
                        padding: '20px',
                        textAlign: 'center',
                        color: 'white',
                        fontSize: '1.2rem',
                        marginTop: '50%'
                    }}>
                        No fitness data available
                    </div>
                ) : (
                    <div className="activity-graphs-wrapper">
                        <ActivityGraph data={activityData1} label="Distance/min" color="#0eaa0b" />
                        <ActivityGraph data={activityData2} label="Heart Rate" color="#3b82f6" />
                        <ActivityGraph data={activityData3} label="Peak Speed" color="#ec4899" />
                        <ActivityGraph data={activityData4} label="HR Zone 4+5" color="#f59e0b" />
                        <ActivityGraph data={activityData5} label="Intensity" color="#8b5cf6" />
                    </div>
                )}
            </IonContent>
        </IonPage>
    );
};

export default ActivityGraphComparison;
